import { Injectable, computed, inject, signal } from '@angular/core';
import { FirebaseAuthService, PermissionType, PlanType, RolType } from '@dataclouder/app-auth';
import { EntityCommunicationService, getLangDesc } from '@dataclouder/ngx-core';
import { User } from '../models/user.model';
import { isEmpty } from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class UserService extends EntityCommunicationService<User> {
  private firebaseAuthService = inject(FirebaseAuthService);

  private readonly USER_STORAGE_KEY = 'user';
  private readonly API_URL = 'api/user';

  // State Signals
  private userState = signal<User | null>(null);
  public isLoading = signal<boolean>(false);

  // Public Signals & Computed Signals
  public user = this.userState.asReadonly();
  public isPremium = computed(() => this.user()?.claims?.plan?.type === PlanType.Premium);
  public isAdmin = computed(() => this.hasRole(RolType.Admin));
  public isTester = computed(() => this.hasRole(RolType.Tester));
  public isFollower = computed(() => this.hasRole(RolType.Follower));

  constructor() {
    super('user');
    this.loadUserFromLocal();
  }

  // --- Public API for Data Loading and Caching ---

  public async refreshUserFromServer(force = false): Promise<void> {
    if (this.userState() && !force) {
      console.log('User already loaded from signal state.');
      return;
    }

    this.isLoading.set(true);
    try {
      const user = await this.httpService.get<User>(`${this.API_URL}/logged`);
      this._updateUserStateAndLocal(user);
      console.log('User loaded from API');
    } catch (error) {
      console.error('Failed to load user from API', error);
      throw error; // Rethrow to allow caller to handle
    } finally {
      this.isLoading.set(false);
    }
  }

  public clearUser(): void {
    this.userState.set(null);
    this._clearUserFromLocal();
  }

  public getUserDataInformation(): string {
    const user = this.user();
    if (!user) {
      return '';
    }

    const settings = user.settings;
    const level = user.languageProgress[settings.targetLanguage]?.level;

    const dataDefinitions: { key: string; description: string; value: unknown }[] = [
      { key: 'name', description: 'Name of the user', value: user.personalData?.emotionalName || user.personalData?.firstname || 'Usuario' },
      { key: 'gender', description: 'Gender of the user', value: user.personalData?.gender },
      { key: 'baseLang', description: 'Base language of the user', value: getLangDesc(settings.baseLanguage, 'en') },
      { key: 'targetLang', description: 'Target language of the user', value: getLangDesc(settings.targetLanguage, 'en') },
      { key: 'level', description: 'Level of the user', value: level },
      // { key: 'levelDesc', description: 'Level description of the user', value: level ? LevelDescription[level] : undefined },
    ];

    if (user?.personalData?.birthday) {
      dataDefinitions.push({
        key: 'age',
        description: 'Age of the user',
        value: `${new Date().getFullYear() - new Date(user.personalData.birthday).getFullYear()}`,
      });
    }

    return dataDefinitions
      .filter((item) => item.value !== null && item.value !== undefined && item.value !== '')
      .map((item) => `[${item.description}: ${item.value}]`)
      .join('\n');
  }

  // --- Public API for User Data Updates ---

  public async updatePartialUserServer(user: Partial<User>): Promise<User> {
    this.isLoading.set(true);
    try {
      const id = this.user()?._id || this.user()?.id;
      const updatedUser = await this.partialUpdate(id, user);

      this._updateUserStateAndLocal(updatedUser);
      console.log('User updated successfully');
      return updatedUser;
    } catch (error) {
      console.error('Failed to update user', error);
      throw error;
    } finally {
      this.isLoading.set(false);
    }
  }

  public updateLocalUser(user: Partial<User>): void {
    this.userState.update((current) => {
      const updated = { ...current, ...user } as User;
      this._saveUserToLocal(updated);
      return updated;
    });
  }

  public async applyPromoCode(promoCode: string): Promise<any> {
    // FIXME: Replace with actual endpoint, e.g. 'user/promo-code'
    const response: any = await this.httpService.get(`user/promo/${promoCode}`);
    await this.updateUserClaimsAndRefresh(response.claims);
    return response;
  }

  public async updateUserClaimsAndRefresh(claims: any): Promise<void> {
    await this.updatePartialUserServer({ claims });
    await this.firebaseAuthService.refreshToken();
  }

  public async deleteUserAccountAsync(): Promise<void> {
    // FIXME: Replace with actual endpoint, e.g. 'user'
    await this.httpService.delete('user');
    this.clearUser();
  }

  // --- Private Helper Methods ---

  private _updateUserStateAndLocal(user: User): void {
    this.userState.set(user);
    this._saveUserToLocal(user);
  }

  private loadUserFromLocal(): void {
    const userJson = localStorage.getItem(this.USER_STORAGE_KEY);
    if (userJson) {
      const user = JSON.parse(userJson);
      if (!isEmpty(user)) {
        this.userState.set(user);
      } else {
        alert('Viene vacio no debe');
      }
    }
  }

  private _saveUserToLocal(user: User | null): void {
    if (user) {
      localStorage.setItem(this.USER_STORAGE_KEY, JSON.stringify(user));
    } else {
      this._clearUserFromLocal();
    }
  }

  private _clearUserFromLocal(): void {
    localStorage.removeItem(this.USER_STORAGE_KEY);
  }

  // --- Permissions and Roles ---

  public hasRole(rol: RolType): boolean {
    const roles = this.user()?.claims?.roles;
    if (roles && rol in roles) {
      const expiry = roles[rol];
      if (expiry === null) return true; // No expiration
      return new Date(expiry) > new Date();
    }
    return false;
  }

  public hasPermission(permission: PermissionType): boolean {
    const permissions = this.user()?.claims?.permissions;
    if (permissions && permission in permissions) {
      const expiry = permissions[permission];
      if (expiry === null) return true; // No expiration
      return new Date(expiry.exp) > new Date();
    }
    return false;
  }

  public isFollowerOrBetaOrPremimPlan(isBeta: boolean): boolean {
    return this.isFollower() || (isBeta && this.isTester()) || this.isPremium();
  }

  // --- Other Methods ---

  public async checkStatusToken(): Promise<any> {
    // FIXME: Replace with actual endpoint, e.g. 'user/status'
    return this.httpService.get('user/check-status-token');
  }
}
